import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { useAdminPWA } from '@/hooks/useAdminPWA';

const AdminLogin = () => {
  useAdminPWA();
  const { signIn, resetPassword, updatePassword, user, loading } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  // Check for password reset tokens and redirect if already logged in
  useEffect(() => {
    const handlePasswordReset = async () => {
      // Check URL parameters for password reset tokens
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('access_token');
      const refreshToken = urlParams.get('refresh_token');
      const type = urlParams.get('type');
      
      console.log('🔍 Checking for recovery tokens:', { type, hasAccessToken: !!accessToken, hasRefreshToken: !!refreshToken });
      
      if (type === 'recovery' && accessToken && refreshToken) {
        setIsLoading(true);
        setError('');
        
        try {
          // Exchange the tokens for a valid session
          console.log('🔄 Exchanging recovery tokens for session...');
          const { data: session, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (sessionError) {
            console.error('❌ Session exchange failed:', sessionError);
            if (sessionError.message.includes('expired') || sessionError.message.includes('invalid')) {
              setError('Password reset link has expired. Please request a new one.');
            } else {
              setError('Invalid password reset link. Please request a new one.');
            }
            // Clear the URL parameters
            window.history.replaceState(null, '', window.location.pathname);
          } else if (session?.session) {
            console.log('✅ Recovery session established successfully');
            setShowUpdateForm(true);
            // Clear the URL parameters but keep the user on this page
            window.history.replaceState(null, '', window.location.pathname);
          } else {
            setError('Unable to establish recovery session. Please request a new password reset.');
            window.history.replaceState(null, '', window.location.pathname);
          }
        } catch (error) {
          console.error('❌ Unexpected error during token exchange:', error);
          setError('An error occurred processing the password reset link. Please try again.');
          window.history.replaceState(null, '', window.location.pathname);
        } finally {
          setIsLoading(false);
        }
      } else if (user && !loading) {
        navigate('/admin');
      }
    };

    handlePasswordReset();
  }, [user, loading, navigate]);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const { error } = await signIn(email, password);
    
    if (error) {
      setError(error.message);
    } else {
      navigate('/admin');
    }
    
    setIsLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setResetMessage('');

    const formData = new FormData(e.currentTarget);
    const email = formData.get('reset-email') as string;

    const { error } = await resetPassword(email);
    
    if (error) {
      setError(error.message);
    } else {
      setResetMessage('Password reset email sent! Check your inbox for instructions.');
    }
    
    setIsLoading(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const formData = new FormData(e.currentTarget);
    const password = formData.get('new-password') as string;
    const confirmPassword = formData.get('confirm-password') as string;

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    const { error } = await updatePassword(password);
    
    if (error) {
      setError(error.message);
    } else {
      setResetMessage('Password updated successfully! You can now sign in with your new password.');
      setShowUpdateForm(false);
    }
    
    setIsLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="Admin Login - Safeway Roofing CMS"
        description="Admin login for Safeway Roofing content management system"
        canonical="https://quote.safewayroofingandchimney.com/admin/login"
      />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-foreground">
              Safeway Roofing CMS
            </CardTitle>
            <CardDescription>
              Admin access to manage projects and content
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showUpdateForm ? (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold">Update Your Password</h3>
                  <p className="text-sm text-muted-foreground">Enter your new password below</p>
                </div>
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      name="new-password"
                      type="password"
                      required
                      placeholder="••••••••"
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      name="confirm-password"
                      type="password"
                      required
                      placeholder="••••••••"
                      minLength={6}
                    />
                  </div>
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  {resetMessage && (
                    <Alert>
                      <AlertDescription>{resetMessage}</AlertDescription>
                    </Alert>
                  )}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Update Password
                  </Button>
                </form>
                {resetMessage && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setShowUpdateForm(false);
                        setError('');
                        setResetMessage('');
                      }}
                      className="text-sm text-primary hover:underline"
                    >
                      Back to Sign In
                    </button>
                  </div>
                )}
              </div>
            ) : !showResetForm ? (
              <div className="space-y-4">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="admin@safewayroofing.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      required
                      placeholder="••••••••"
                    />
                  </div>
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Sign In
                  </Button>
                </form>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowResetForm(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      name="reset-email"
                      type="email"
                      required
                      placeholder="admin@safewayroofing.com"
                    />
                  </div>
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  {resetMessage && (
                    <Alert>
                      <AlertDescription>{resetMessage}</AlertDescription>
                    </Alert>
                  )}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Send Reset Email
                  </Button>
                </form>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetForm(false);
                      setError('');
                      setResetMessage('');
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    Back to Sign In
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AdminLogin;